import { useEffect } from 'react';

interface V1BackgroundStyleProviderProps {
  style?: {
    backgroundColor?: string;
    backgroundUrl?: string;
    backgroundPosition?: string;
    backgroundTiled?: boolean;
  };
}

/**
 * V1BackgroundStyleProvider applies V1-specific background styles to the body element.
 * This matches the exact Rails logic for V1 events to ensure visual consistency.
 * 
 * IMPORTANT: This component is ONLY for V1 events and replicates the exact
 * behavior from app/views/teams/styles/_style.html.haml
 */
export const V1BackgroundStyleProvider: React.FC<V1BackgroundStyleProviderProps> = ({ style }) => {
  useEffect(() => {
    if (!style) return;

    const body = document.body;
    
    // Store original styles to restore on cleanup
    const originalStyles = {
      backgroundColor: body.style.backgroundColor,
      backgroundImage: body.style.backgroundImage,
      backgroundPosition: body.style.backgroundPosition,
      backgroundRepeat: body.style.backgroundRepeat,
      backgroundSize: body.style.backgroundSize,
      backgroundAttachment: body.style.backgroundAttachment,
    };

    // Apply V1-specific styles matching Rails logic exactly
    body.style.margin = '0';
    body.style.padding = '0';
    body.style.minHeight = '100vh';

    // Apply background color
    if (style.backgroundColor) {
      body.style.backgroundColor = style.backgroundColor;
    }

    // Apply background image with V1-specific logic
    if (style.backgroundUrl) {
      body.style.backgroundImage = `url(${style.backgroundUrl})`;

      // V1 Background position logic from Rails:
      // - default is 'left'
      // - 'center' becomes '50%'
      // - all positions align to 'top' except 'expand' which uses 'center center'
      let bgPosition = 'left top'; // default
      if (style.backgroundPosition === 'left') {
        bgPosition = 'left top';
      } else if (style.backgroundPosition === 'center') {
        bgPosition = '50% top';
      } else if (style.backgroundPosition === 'right') {
        bgPosition = 'right top';
      } else if (style.backgroundPosition === 'expand') {
        bgPosition = 'center center';
      }
      body.style.backgroundPosition = bgPosition;

      // V1 Background repeat logic:
      // if background_tiled is true, use 'repeat', otherwise 'no-repeat'
      body.style.backgroundRepeat = style.backgroundTiled ? 'repeat' : 'no-repeat';

      // V1 Background size logic from Rails:
      // - Default when no tiling/position: 'cover'
      // - When tiled OR position is present: 'inherit'
      // - When position is 'expand': override to 'cover'
      let backgroundSize = 'cover'; // default
      if (style.backgroundTiled || style.backgroundPosition) {
        backgroundSize = 'inherit';
      }
      if (style.backgroundPosition === 'expand') {
        backgroundSize = 'cover';
      }
      body.style.backgroundSize = backgroundSize;

      // V1 uses 'initial' for background-attachment
      body.style.backgroundAttachment = 'initial';
    }

    // Make sure containers are transparent
    const mainContainer = document.getElementById('main-container');
    const mainContent = document.getElementById('main-content');
    const publicEventRoot = document.getElementById('public-event-root');

    if (mainContainer) mainContainer.style.backgroundColor = 'transparent';
    if (mainContent) mainContent.style.backgroundColor = 'transparent';
    if (publicEventRoot) publicEventRoot.style.backgroundColor = 'transparent';

    // Cleanup function to restore original styles
    return () => {
      Object.entries(originalStyles).forEach(([property, value]) => {
        (body.style as any)[property] = value;
      });
    };
  }, [style]);

  return null;
};